import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Trophy, CheckCircle, AlertCircle } from 'lucide-react';
import Button from '../UI/Button';
import tournamentService from '../../services/tournamentService';
import toast from 'react-hot-toast';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px;
  border-bottom: 1px solid #2d2d44;
  background: linear-gradient(90deg, #2d2d44 0%, #1a1a2e 100%);
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #b8b8d1;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #ff6b6b;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
`;

const MatchInfo = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
`;

const MatchTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: center;
`;

const TeamsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
`;

const TeamCard = styled.div`
  flex: 1;
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  transition: all 0.3s ease;
  
  ${props => props.isWinner && `
    border-color: #4ecdc4;
    background: linear-gradient(135deg, rgba(78, 205, 196, 0.1) 0%, rgba(78, 205, 196, 0.05) 100%);
    box-shadow: 0 0 20px rgba(78, 205, 196, 0.3);
  `}
`;

const TeamName = styled.div`
  color: ${props => props.isWinner ? '#4ecdc4' : '#ffffff'};
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const TeamId = styled.div`
  color: #6c6c8a;
  font-size: 14px;
  margin-bottom: 15px;
`;

const TeamScore = styled.div`
  color: ${props => props.isWinner ? '#4ecdc4' : '#b8b8d1'};
  font-size: 24px;
  font-weight: 700;
`;

const VSContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const VSText = styled.div`
  color: #6c6c8a;
  font-size: 16px;
  font-weight: 600;
`;

const WinnerBadge = styled.div`
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const FormContainer = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
`;

const FormTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  text-align: center;
`;

const SelectContainer = styled.div`
  margin-bottom: 20px;
`;

const SelectLabel = styled.label`
  display: block;
  color: #b8b8d1;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  padding: 12px 15px;
  color: #ffffff;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 0 2px rgba(0, 212, 255, 0.2);
  }
  
  option {
    background: #1a1a2e;
    color: #ffffff;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin: 20px 0;
  padding: 15px;
  background: linear-gradient(90deg, rgba(255, 107, 107, 0.1) 0%, transparent 100%);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SuccessMessage = styled.div`
  color: #4ecdc4;
  font-size: 14px;
  margin: 20px 0;
  padding: 15px;
  background: linear-gradient(90deg, rgba(78, 205, 196, 0.1) 0%, transparent 100%);
  border: 1px solid rgba(78, 205, 196, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  color: #b8b8d1;
`;

const RecordResultModal = ({ 
  show, 
  match, 
  onClose, 
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [winnerTeamId, setWinnerTeamId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (show) {
      setWinnerTeamId('');
      setError('');
      setSuccess('');
    }
  }, [show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!winnerTeamId) {
      setError('لطفاً تیم برنده را انتخاب کنید');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await tournamentService.recordMatchResult(match.matchId, winnerTeamId);
      setSuccess('نتیجه مسابقه با موفقیت ثبت شد');
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        if (onClose) onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'خطا در ثبت نتیجه مسابقه');
      console.error('Error recording match result:', err);
    } finally {
      setLoading(false);
    }
  };


  if (!show || !match) return null;

  const isTeamOneWinner = match.winnerName === match.teamOneName;
  const isTeamTwoWinner = match.winnerName === match.teamTwoName;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <Trophy size={24} />
            ثبت نتیجه مسابقه
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <MatchInfo>
            <MatchTitle>مسابقه #{match.matchId}</MatchTitle>
            <TeamsContainer>
              <TeamCard isWinner={isTeamOneWinner}>
                <TeamName isWinner={isTeamOneWinner}>
                  {match.teamOneName || 'TBD'}
                </TeamName>
                <TeamId>تیم ID: {match.teamOneId}</TeamId>
                <TeamScore isWinner={isTeamOneWinner}>
                  {match.teamOneScore || 0}
                </TeamScore>
                {isTeamOneWinner && (
                  <WinnerBadge>
                    <CheckCircle size={14} />
                    برنده
                  </WinnerBadge>
                )}
              </TeamCard>

              <VSContainer>
                <VSText>VS</VSText>
              </VSContainer>

              <TeamCard isWinner={isTeamTwoWinner}>
                <TeamName isWinner={isTeamTwoWinner}>
                  {match.teamTwoName || 'TBD'}
                </TeamName>
                <TeamId>تیم ID: {match.teamTwoId}</TeamId>
                <TeamScore isWinner={isTeamTwoWinner}>
                  {match.teamTwoScore || 0}
                </TeamScore>
                {isTeamTwoWinner && (
                  <WinnerBadge>
                    <CheckCircle size={14} />
                    برنده
                  </WinnerBadge>
                )}
              </TeamCard>
            </TeamsContainer>
          </MatchInfo>

          {match.status !== 'Finished' && (
            <FormContainer>
              <form onSubmit={handleSubmit}>
                <FormTitle>انتخاب تیم برنده</FormTitle>
                
                <SelectContainer>
                  <SelectLabel>تیم برنده</SelectLabel>
                  <Select
                    value={winnerTeamId}
                    onChange={(e) => setWinnerTeamId(e.target.value)}
                    required
                  >
                    <option value="">-- انتخاب تیم برنده --</option>
                    <option value={match.teamOneId}>{match.teamOneName}</option>
                    <option value={match.teamTwoId}>{match.teamTwoName}</option>
                  </Select>
                </SelectContainer>

                {error && (
                  <ErrorMessage>
                    <AlertCircle size={16} />
                    {error}
                  </ErrorMessage>
                )}

                {success && (
                  <SuccessMessage>
                    <CheckCircle size={16} />
                    {success}
                  </SuccessMessage>
                )}

                <ActionButtons>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={onClose}
                    disabled={loading}
                    icon={<X size={16} />}
                  >
                    انصراف
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    disabled={loading || !winnerTeamId}
                    icon={<CheckCircle size={16} />}
                  >
                    {loading ? 'در حال ثبت...' : 'ثبت نتیجه'}
                  </Button>
                </ActionButtons>
              </form>
            </FormContainer>
          )}

          {match.status === 'Finished' && (
            <div style={{ 
              textAlign: 'center', 
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1) 0%, transparent 100%)',
              border: '1px solid rgba(78, 205, 196, 0.3)',
              borderRadius: '12px'
            }}>
              <CheckCircle size={48} color="#4ecdc4" style={{ marginBottom: '15px' }} />
              <div style={{ color: '#4ecdc4', fontSize: '18px', fontWeight: '600', marginBottom: '10px' }}>
                مسابقه تکمیل شده
              </div>
              <div style={{ color: '#b8b8d1', fontSize: '14px' }}>
                نتیجه این مسابقه قبلاً ثبت شده است
              </div>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default RecordResultModal;
